import { CurrencyFormatting, DateFormatting, NumberFormatting } from './application.dictionary'
import { ChatDisplayTypes } from './chat.dictionary'
import { ComponentIdentifierEnum, ComponentType, FormFieldsEnum } from './template.dictionary'

import { SuggestedPromptInput } from '../utils/chat.utils'

// ACCESSIBILITY
export interface LabelAccessibilityProps {
  label: string | null
}

export interface DecorativeAccessibilityProps {
  decorative: boolean
}

export interface ImageAccessibilityProps {
  decorative: boolean
  alt: string | null
}

export interface RoleAccessibilityProps {
  role: string | null
}

export interface ZoneAccessibilityProps {
  role: string | null
}

// BUTTON
export const ButtonVariants = ['fill', 'ghost', 'text'] as const
export type ButtonVariant = (typeof ButtonVariants)[number]

export const IconPositions = ['center', 'left', 'right'] as const
export type IconPosition = '' | (typeof IconPositions)[number]

export interface ButtonProps {
  label: string
  type: ButtonVariant
  disabled: string
  icon: {
    value: string
    library?: 'phosphor' | 'lucide' | 'material' | 'media'
    variant?: string
    appId: string | null
    resDefId: string | null
  }
  iconPosition: IconPosition
  dataTestId: string
  hide: string
  accessibility: LabelAccessibilityProps | null
}

// TEXT
export interface TextProps {
  text: string
  isComputedText: boolean
  dataTestId: string
  numberFormatting: NumberFormatting
  dateFormatting: DateFormatting
  currencyFormatting: CurrencyFormatting
  hide: string
  accessibility: DecorativeAccessibilityProps | null
}

// LINK
export interface LinkProps {
  text: string
  isComputedText: boolean
  url: string
  dataTestId: string
  numberFormatting: NumberFormatting
  dateFormatting: DateFormatting
  currencyFormatting: CurrencyFormatting
  hide: string
  accessibility: DecorativeAccessibilityProps | null
}

/** Tells whether `value` holds a literal or a data key path. */
export enum FileSourceValueType {
  STATIC = 'STATIC',
  VARIABLE = 'VARIABLE',
}

export interface FileSource {
  sourceType: FileSourceType
  /** Omitted for `MEDIA_LIBRARY` — a media library key is always literal. */
  valueType?: FileSourceValueType
  value: string
  /** `MEDIA_LIBRARY` only. */
  appId?: string | null
  /** `MEDIA_LIBRARY` only. */
  resDefId?: string | null
  /** Data model reference of a `VARIABLE` value. Written by the designer, never read at runtime. */
  referencePath?: string
}

/**
 * Pre-6.1 keys. The 6.1 format ignores them; they are read only in file-source compatibility
 * mode (see `config/file-source-compatibility.ts`), which resolves sources the pre-6.1 way.
 */
export interface LegacyFileSourceKeys {
  /** Document preview / download action, replaced by `sourceType` + `valueType` in 6.1. */
  location?: DocumentPreviewLocationType
  /** Image, replaced by `sourceType` + `valueType` in 6.1. */
  sourceLocation?: ImageSourceLocationOptions
}

// IMAGE
/** Pre-6.1 image location. Read only in file-source compatibility mode. */
export enum ImageSourceLocationOptions {
  EXTERNAL = 'external',
  PROCESS = 'process',
  MEDIA_LIBRARY = 'media_library',
}

export interface ImageProps {
  image: FileSource & LegacyFileSourceKeys
  placeholderUrl: string
  dataTestId: string
  // ? - needed?
  sourceKey: string
  imageSrc: string
  hide: string
  accessibility: ImageAccessibilityProps | null
}

// FILE UPLOAD
export enum LoadingBehavior {
  None = 'NONE',
  Overlay = 'OVERLAY',
}

export interface FileUploadProps {
  label: string
  type: ButtonVariant
  accept: string
  invalidFileType: string
  maxFileSize: number
  fileSizeExceeded: string
  dataTestId: string
  disabled: string
  hide: string
  accessibility: LabelAccessibilityProps | null
  showSuccess: boolean
  successMessage: string
  showError: boolean
  errorMessage: string
  loading: LoadingBehavior
  loadingMessage: string
  icon: {
    value: string
    library?: 'phosphor' | 'lucide' | 'material' | 'media'
    variant?: string
    appId: string | null
    resDefId: string | null
  }
  iconPosition: IconPosition
}

export interface FileDownloadProps {
  label: string
  saveAsFileName: string
  toastNotificationSuccess: boolean
  toastNotificationError: boolean
  successMessage: string
  errorMessage: string
  loading: LoadingBehavior
  loadingMessage: string
  disabled: string
  hide: string
  dataTestId: string
  type: ButtonVariant
  accessibility: LabelAccessibilityProps | null
  icon: {
    value: string
    appId: string | null
    resDefId: string | null
  } | null
  iconPosition: IconPosition | null
}

export interface BulkFileUploadProps {
  title: string
  subtitle: string
  browseLabel: string
  uploadLabel: string
  resetLabel: string
  accept: string
  invalidFileType: string
  maxFilesSize: number
  filesSizeExceeded: string
  maxFiles: number
  filesExceeded: string
  dataTestId: string
  disabled: string
  hide: string
  showSuccess: boolean
  successMessage: string
  showError: boolean
  errorMessage: string
  loading: LoadingBehavior
  loadingMessage: string
}

// FILE SOURCE
export enum FileSourceType {
  MEDIA_LIBRARY = 'MEDIA_LIBRARY',
  DOCUMENT_PLUGIN_DOWNLOAD_PATH = 'DOCUMENT_PLUGIN_DOWNLOAD_PATH',
  DOCUMENT_PLUGIN = 'DOCUMENT_PLUGIN',
  URL = 'URL',
  BASE64 = 'BASE64',
}

// DOCUMENT PREVIEW
export const enum DocumentPreviewDisplayMode {
  INLINE = 'INLINE',
  MODAL = 'MODAL',
}

export const enum DocumentPreviewDefaultView {
  COLLAPSED = 'COLLAPSED',
  EXPANDED = 'EXPANDED',
}

/** Pre-6.1 document source. Read only in file-source compatibility mode. */
export enum DocumentPreviewSourceType {
  MEDIA_LIBRARY = 'MEDIA_LIBRARY',
  PROCESS_DATA = 'PROCESS_DATA',
  STATIC = 'STATIC',
}

/** Pre-6.1 document location. Read only in file-source compatibility mode. */
export enum DocumentPreviewLocationType {
  URL = 'URL',
  DOCUMENT_PLUGIN = 'DOCUMENT_PLUGIN',
}

export interface DocumentPreviewProps {
  title: string
  subtitle: string
  hasAccordion: boolean
  displayMode: DocumentPreviewDisplayMode
  defaultView: DocumentPreviewDefaultView
  document: FileSource & LegacyFileSourceKeys
  dataTestId: string
  hide: string
  accessibility: DecorativeAccessibilityProps | LabelAccessibilityProps | null
}

// MESSAGE
export type MessageType = 'success' | 'warning' | 'error' | 'info'
export const DefaultMessageType: MessageType = 'success'

export type MessageDisplay = 'text' | 'border' | 'fill'
export const DefaultMessageDisplay: MessageDisplay = 'border'

export interface MessageProps {
  messageType: MessageType
  messageStyle: MessageDisplay
  message: string
  hide: string
  dataTestId: string
  numberFormatting: NumberFormatting
  dateFormatting: DateFormatting
  currencyFormatting: CurrencyFormatting
  accessibility: DecorativeAccessibilityProps | null
}

// TOOLTIP
export interface TooltipProps {
  hide: string
  text: string
  dataTestId: string
  icon?: {
    value: string
    appId: string | null
    resDefId: string | null
  }
  accessibility: LabelAccessibilityProps | DecorativeAccessibilityProps | null
}

// SEPARATOR
export interface SeparatorProps {
  hide: string
  dataTestId: string
}

// PROGRESS BAR
export interface ProgressBarProps {
  hide: string
  progress: string
  isComputedProgress: boolean
  dataTestId: string
  accessibility: LabelAccessibilityProps | null
}

// MARKDOWN
export interface MarkdownProps {
  text: string
  hide: string
  dataTestId: string
  numberFormatting: NumberFormatting
  dateFormatting: DateFormatting
  currencyFormatting: CurrencyFormatting
  accessibility: LabelAccessibilityProps | DecorativeAccessibilityProps | null
}

// STATS_CARD
export type StatsTrend = 'up' | 'down' | 'neutral'

export interface StatsCardProps {
  label: string
  value: string
  isComputedValue?: boolean
  delta?: string
  trend?: StatsTrend
  icon: {
    value: string
    library?: 'phosphor' | 'lucide' | 'material' | 'media'
    variant?: string
    appId: string | null
    resDefId: string | null
  } | null
  caption?: string
  hide: string
  dataTestId: string
}

// BADGE
export const BadgeVariants = ['neutral', 'success', 'warning', 'error', 'info'] as const
export type BadgeVariant = (typeof BadgeVariants)[number]

export interface BadgeProps {
  label: string
  variant: BadgeVariant
  icon: {
    value: string
    library?: 'phosphor' | 'lucide' | 'material' | 'media'
    variant?: string
    appId: string | null
    resDefId: string | null
  } | null
  removable: boolean
  hide: string
  dataTestId: string
}

// AVATAR
export const AvatarShapes = ['circle', 'square'] as const
export type AvatarShape = (typeof AvatarShapes)[number]

export const AvatarSizes = ['sm', 'md', 'lg'] as const
export type AvatarSize = (typeof AvatarSizes)[number]

export const AvatarStatusColors = ['online', 'busy', 'away', 'offline'] as const
export type AvatarStatusColor = (typeof AvatarStatusColors)[number]

export interface AvatarProps {
  image: {
    value: string
    library?: 'phosphor' | 'lucide' | 'material' | 'media'
    variant?: string
    appId: string | null
    resDefId: string | null
  } | null
  initials: string
  shape: AvatarShape
  size: AvatarSize
  statusDot: boolean
  statusColor: AvatarStatusColor
  hide: string
  dataTestId: string
}

// ALERT
export const AlertVariants = ['info', 'success', 'warning', 'error'] as const
export type AlertVariant = (typeof AlertVariants)[number]

export interface AlertProps {
  title: string
  text: string
  variant: AlertVariant
  icon: {
    value: string
    library?: 'phosphor' | 'lucide' | 'material' | 'media'
    variant?: string
    appId: string | null
    resDefId: string | null
  } | null
  dismissible: boolean
  hide: string
  dataTestId: string
}

// RATING
export const RatingSizes = ['sm', 'md', 'lg'] as const
export type RatingSize = (typeof RatingSizes)[number]

export interface RatingProps {
  /** Static value, or a `${path}` expression resolved at runtime (e.g. `${payload.score}`). */
  value: string
  max: number
  size: RatingSize
  readonly: boolean
  hide: string
  dataTestId: string
}

// TIMELINE
export const TimelineItemStatuses = ['default', 'success', 'warning', 'error'] as const
export type TimelineItemStatus = (typeof TimelineItemStatuses)[number]

export interface TimelineItem {
  title: string
  description?: string
  status?: TimelineItemStatus
  icon?: {
    value: string
    library?: 'phosphor' | 'lucide' | 'material' | 'media'
    variant?: string
    appId: string | null
    resDefId: string | null
  }
}

export interface TimelineProps {
  items: TimelineItem[]
  /** When true, `itemsSource` is resolved at runtime instead of `items`. */
  isDynamicDataSource: boolean
  /** `${path}` expression resolving to a `TimelineItem[]`. Used when `isDynamicDataSource` is true. */
  itemsSource: string
  activeIndex?: number
  hide: string
  dataTestId: string
}

// PROGRESS_RING
export const ProgressRingSizes = ['sm', 'md', 'lg'] as const
export type ProgressRingSize = (typeof ProgressRingSizes)[number]

export const ProgressRingVariants = ['primary', 'success', 'warning', 'error'] as const
export type ProgressRingVariant = (typeof ProgressRingVariants)[number]

export interface ProgressRingProps {
  /** Static value, or a `${path}` expression resolved at runtime (e.g. `${payload.progress}`). */
  value: string
  max: number
  size: ProgressRingSize
  variant: ProgressRingVariant
  showLabel: boolean
  hide: string
  dataTestId: string
}

// DESCRIPTION_LIST
export interface DescriptionListItem {
  label: string
  value: string
}

export const DescriptionListLayouts = ['horizontal', 'stacked'] as const
export type DescriptionListLayout = (typeof DescriptionListLayouts)[number]

export interface DescriptionListProps {
  items: DescriptionListItem[]
  /** When true, `itemsSource` is resolved at runtime instead of `items`. */
  isDynamicDataSource: boolean
  /** `${path}` expression resolving to a `DescriptionListItem[]`. Used when `isDynamicDataSource` is true. */
  itemsSource: string
  layout: DescriptionListLayout
  hide: string
  dataTestId: string
}

// TAG_GROUP
export const TagVariants = ['neutral', 'primary', 'success', 'warning', 'error'] as const
export type TagVariant = (typeof TagVariants)[number]

export interface TagGroupTag {
  label: string
  variant?: TagVariant
}

export const TagGroupSizes = ['sm', 'md'] as const
export type TagGroupSize = (typeof TagGroupSizes)[number]

export interface TagGroupProps {
  tags: TagGroupTag[]
  /** When true, `tagsSource` is resolved at runtime instead of `tags`. */
  isDynamicDataSource: boolean
  /** `${path}` expression resolving to a `TagGroupTag[]`. Used when `isDynamicDataSource` is true. */
  tagsSource: string
  size: TagGroupSize
  hide: string
  dataTestId: string
}

// CHART
export const ChartTypes = ['bar', 'line', 'pie', 'doughnut', 'radar'] as const
export type ChartType = (typeof ChartTypes)[number]

export interface ChartDataset {
  label: string
  data: number[]
  color?: string
}

export interface ChartProps {
  type: ChartType
  labels: string[]
  datasets: ChartDataset[]
  /** When true, `labelsSource`/`datasetsSource` are resolved at runtime instead of `labels`/`datasets`. */
  isDynamicDataSource: boolean
  /** `${path}` expression resolving to a `string[]` of labels. Used when `isDynamicDataSource` is true. */
  labelsSource: string
  /** `${path}` expression resolving to a `ChartDataset[]`. Used when `isDynamicDataSource` is true. */
  datasetsSource: string
  showLegend: boolean
  title: string
  hide: string
  dataTestId: string
}

// STEP_INDICATOR
export const StepIndicatorOrientations = ['horizontal', 'vertical'] as const
export type StepIndicatorOrientation = (typeof StepIndicatorOrientations)[number]

export interface StepIndicatorStep {
  label: string
}

export interface StepIndicatorProps {
  steps: StepIndicatorStep[]
  /** When true, `stepsSource` is resolved at runtime instead of `steps`. */
  isDynamicDataSource: boolean
  /** `${path}` expression resolving to a `StepIndicatorStep[]`. Used when `isDynamicDataSource` is true. */
  stepsSource: string
  activeIndex: number
  hide: string
  dataTestId: string
}

// EMPTY_STATE
export interface EmptyStateProps {
  icon: {
    value: string
    library?: 'phosphor' | 'lucide' | 'material' | 'media'
    variant?: string
    appId: string | null
    resDefId: string | null
  } | null
  heading: string
  description: string
  actionLabel: string
  hide: string
  dataTestId: string
}

// SKELETON
export const SkeletonVariants = ['text', 'rectangular', 'circular'] as const
export type SkeletonVariant = (typeof SkeletonVariants)[number]

export interface SkeletonProps {
  variant: SkeletonVariant
  lines: number
  animated: boolean
  hide: string
  dataTestId: string
}

// CONTAINER
export interface ContainerProps {
  screenTitle: string
  hide: string
  dataTestId: string
  analytics: string
  analyticsCustomPayload: string
  accessibility: RoleAccessibilityProps | null
}

// CARD
export const enum CardDefaultCollapseState {
  COLLAPSED = 'COLLAPSED',
  EXPANDED = 'EXPANDED',
}

export interface CardProps {
  hasAccordion: boolean
  defaultCollapseState: CardDefaultCollapseState
  screenTitle: string
  title: string
  subtitle: string
  headerIcon: {
    value: string
    library?: 'phosphor' | 'lucide' | 'material' | 'media'
    variant?: string
    appId: string | null
    resDefId: string | null
  } | null
  hasHeaderActions: boolean
  hasFooterActions: boolean
  dataTestId: string
  hide: string
  analytics: string
  analyticsCustomPayload: string
}

// FORM
export enum ValidateOnOptions {
  SUBMIT = 'submit',
  BLUR = 'blur',
}

export interface FormProps {
  label: string
  validateOn: ValidateOnOptions
  disabled: string
  hide: string
  dataTestId: string
}

// COLLECTION
export interface CollectionProps {
  collectionSource: string
  dataTestId: string
  hide: string
}

// COLLECTION PROTOTYPE
export interface CollectionPrototypeProps {
  prototypeIdentifierKey: string
  prototypeIdentifierValue: string
  dataTestId: string
}

// TABLE
export enum SortingDirection {
  Ascending = 'asc',
  Descending = 'desc',
}

export interface DefaultSorting {
  column: string | null
  direction: SortingDirection
}

export interface TableProps {
  source: string
  defaultSorting: DefaultSorting | null
  emptyMessage: string
  pageSize: number | null
  showRowIndex: boolean
  deletableRows: boolean
  hide: string
  dataTestId: string
}

// TABLE ROW
export interface TableRowProps {
  dataTestId: string
}

// TABLE CELL
export interface TableCellProps {
  label: string
  sortable: boolean
  filterable: boolean
  editable: boolean
  dataTestId: string
}

// TASK MANAGEMENT
export interface TaskManagementProps {
  dataTestId: string
  taskManagementView?: {
    resDefId: string
    appId: string
  }
}

// PROCESS RENDERER
export interface ProcessRendererProps {
  dataTestId: string
  process?: {
    resDefId: string
    appId: string
    startParams: Record<string, any>
  }
}

// PAGE
export type NavigationType = 'default' | 'wizard'

export interface PageProps {
  /** Screen name reported to analytics; absent means this screen is not reported. */
  analytics?: string
  analyticsCustomPayload?: string
  title?: string
  screenTitle?: string
  navigationType?: NavigationType
  dataTestId: string
}

// ZONE
export interface ZoneProps {
  /** Screen name reported to analytics; absent means this screen is not reported. */
  analytics?: string
  analyticsCustomPayload?: string
  title?: string
  navigationType?: NavigationType
  dataTestId: string
  accessibility: ZoneAccessibilityProps | null
}

// MODAL
export interface ModalProps {
  /** Screen name reported to analytics; absent means this screen is not reported. */
  analytics?: string
  analyticsCustomPayload?: string
  title?: string
  screenTitle?: string
  subtitle?: string
  canDismissModal: boolean
  closeOnBackdropClick: boolean
  showDismissConfirmationAlert: boolean
  modalDismissAlert: {
    title: string
    message: string
    confirmLabel: string
    cancelLabel: string
  }
  backdropClass: string
  panelClass: string
  dataTestId: string
}

// STEPPTER
export interface StepperProps {
  /** Screen name reported to analytics; absent means this screen is not reported. */
  analytics?: string
  analyticsCustomPayload?: string
  dataTestId: string
}

// STEP
export interface StepProps {
  label: string
  accessibility: LabelAccessibilityProps | null
}

// TAB
export interface TabProps {
  title: string
  selectedIcon: {
    value: string
    appId: string | null
    resDefId: string | null
  }
  unselectedIcon: {
    value: string
    appId: string | null
    resDefId: string | null
  }
  dataTestId: string
  accessibility: LabelAccessibilityProps | null
}

// TAB BAR
export interface TabBarProps {
  /** Screen name reported to analytics; absent means this screen is not reported. */
  analytics?: string
  analyticsCustomPayload?: string
  title?: string
  dataTestId: string
}

// CHAT
export interface ChatProps {
  resetSession?: boolean | null
  welcomeMessage?: string | null
  thinkingMessage?: string | null
  hideHeader?: boolean | null
  displayTypes?: ChatDisplayTypes | null
}

// CHAT HEADER
export interface ChatHeaderProps {
  title?: string | null
  subtitle?: string | null
  showSeparator?: boolean | null
  showIcon?: boolean | null
  hideNewConversation?: boolean | null
  hideHistory?: boolean | null
  newChatLabel?: string | null
  headerIcon?: {
    value: string
    appId: string | null
    resDefId: string | null
  } | null
}

// CHAT AGENT MESSAGE
export interface ChatAgentMessageProps {
  showIcon?: boolean | null
  agentIcon?: {
    value: string
    appId: string | null
    resDefId: string | null
  } | null
}

// CHAT TEXT INPUT
export interface ChatTextInputProps {
  placeholder?: string | null
  maxInputRows?: number | null
}

// CHAT ERROR MESSAGE
export interface ChatErrorMessageProps {
  genericError?: string | null
  regenerateLabel?: string | null
}

// CHAT SUGGESTED PROMPTS
export interface ChatSuggestedPromptsProps {
  title?: string | null
  prompts?: SuggestedPromptInput[] | null
}

// CHAT HISTORY
export interface ChatHistoryProps {
  title?: string | null
}

// CHAT FILE ATTACHMENTS
export interface ChatFileAttachmentsProps {
  accept?: string | null
  maxFiles?: number | null
  maxFilesSize?: number | null
  invalidFileType?: string | null
  filesExceeded?: string | null
  filesSizeExceeded?: string | null
  dataTestId?: string | null
}

// CHAT FILE ITEM
export interface ChatFileItemProps {
  dataTestId?: string | null
}

// CHAT USER ATTACHED FILES
export interface ChatUserAttachedFilesProps {
  dataTestId?: string | null
}

// CHAT USER FILE ITEM
export interface ChatUserFileItemProps {
  dataTestId?: string | null
}

// CHAT MESSAGE ACTIONS
export interface ChatMessageActionsProps {
  showTimestamp?: boolean | null
  showCopyAction?: boolean | null
  showResponseFeedback?: boolean | null
  feedbackReasons?: string[] | null
  dataTestId?: string | null
}

// INPUT
export interface InputProps {
  label: string
  placeholder: string
  hasClear: boolean
  helper: string
  infoPointHelper: boolean
  inputType: string
  prefix: string
  suffix: string
  updateOnBlur: boolean
  format?: boolean
  leftIcon: {
    value: string
    appId: string | null
    resDefId: string | null
  }
  rightIcon: {
    value: string
    appId: string | null
    resDefId: string | null
  }
  disabled: string
  hide: string
  dataTestId: string
  accessibility: LabelAccessibilityProps | null
}

// TEXTAREA
export interface TextareaProps {
  label: string
  placeholder: string
  hasClear: boolean
  helper: string
  infoPointHelper: boolean
  minRows: number
  maxRows: number
  updateOnBlur: boolean
  disabled: string
  hide: string
  dataTestId: string
  accessibility: LabelAccessibilityProps | null
}

// SELECT
export interface SelectProps {
  label: string
  placeholder: string
  hasClear: boolean
  helper: string
  infoPointHelper: boolean
  emptyMessage: string
  hasSearch: boolean
  leftIcon: {
    value: string
    appId: string | null
    resDefId: string | null
  }
  disabled: string
  hide: string
  dataTestId: string
  accessibility: LabelAccessibilityProps | null
}

// MULTI SELECT
export const enum MultiSelectOptionsDisplayModeType {
  COMMA_SEPARATED = 'COMMA_SEPARATED',
  CHIPS_INSIDE = 'CHIPS_INSIDE',
  CHIPS_OUTSIDE = 'CHIPS_OUTSIDE',
}
export interface MultiSelectProps {
  label: string
  placeholder: string
  helper: string
  infoPointHelper: boolean
  emptyMessage: string
  disabled: string
  hide: string
  dataTestId: string
  hasSearch: boolean
  maxRows?: number
  maxSelectable?: string | number
  optionsDisplayMode?: MultiSelectOptionsDisplayModeType
  accessibility: LabelAccessibilityProps | null
}

// CHECKBOX
export interface CheckboxProps {
  label: string
  type: 'bordered' | 'clear'
  helper: string
  infoPointHelper: boolean
  disabled: string
  hide: string
  dataTestId: string
  accessibility: LabelAccessibilityProps | null
}

// RADIO
export interface RadioProps {
  label: string
  type: 'bordered' | 'clear'
  helper: string
  infoPointHelper: boolean
  disabled: string
  hide: string
  dataTestId: string
  accessibility: LabelAccessibilityProps | null
}

// SWITCH
export type SwitchLabelPosition = 'start' | 'end'

export type SwitchType = 'toggle' | 'checkbox'

export interface SwitchProps {
  label: string
  labelPosition: SwitchLabelPosition
  switchType: SwitchType
  helper: string
  infoPointHelper: boolean
  disabled: string
  hide: string
  dataTestId: string
  accessibility: LabelAccessibilityProps | null
}

// DATEPICKER
export interface DatepickerProps {
  label: string
  placeholder: string
  hasClear: boolean
  helper: string
  infoPointHelper: boolean
  minDate: string
  maxDate: string
  matDatepickerMin: string
  matDatepickerMax: string
  disabled: string
  hide: string
  dataTestId: string
  standard: boolean
  allowManualInput?: boolean
  dateFormatting: DateFormatting
  isComputedMinDate: boolean
  isComputedMaxDate: boolean
  accessibility: LabelAccessibilityProps | null
}

// SEGMENTED BUTTON
export interface SegmentedButtonProps {
  label: string
  helper: string
  infoPointHelper: boolean
  disabled: string
  hide: string
  dataTestId: string
  accessibility: LabelAccessibilityProps | null
}

// SLIDER
export interface SliderProps {
  label: string
  showValue: boolean
  minValue: string | number
  isComputedMinValue: boolean
  maxValue: string | number
  isComputedMaxValue: boolean
  step: number
  prefix: string
  suffix: string
  infoPointHelper: boolean
  helper: string
  disabled: string
  hide: string
  dataTestId: string
  format?: boolean
  currencyFormatting: CurrencyFormatting
  accessibility: LabelAccessibilityProps | null
}

type TemplateConfigPropsMap = {
  [ComponentIdentifierEnum.BUTTON]: ButtonProps
  [ComponentIdentifierEnum.TEXT]: TextProps
  [ComponentIdentifierEnum.LINK]: LinkProps
  [ComponentIdentifierEnum.IMAGE]: ImageProps
  [ComponentIdentifierEnum.FILE_UPLOAD]: FileUploadProps
  [ComponentIdentifierEnum.BULK_FILE_UPLOAD]: BulkFileUploadProps
  [ComponentIdentifierEnum.FILE_DOWNLOAD]: FileDownloadProps
  [ComponentIdentifierEnum.DOCUMENT_PREVIEW]: DocumentPreviewProps
  [ComponentIdentifierEnum.MESSAGE]: MessageProps
  [ComponentIdentifierEnum.TOOLTIP]: TooltipProps
  [ComponentIdentifierEnum.SEPARATOR]: SeparatorProps
  [ComponentIdentifierEnum.PROGRESS_BAR]: ProgressBarProps
  [ComponentIdentifierEnum.MARKDOWN]: MarkdownProps
  [ComponentIdentifierEnum.STATS_CARD]: StatsCardProps
  [ComponentIdentifierEnum.BADGE]: BadgeProps
  [ComponentIdentifierEnum.AVATAR]: AvatarProps
  [ComponentIdentifierEnum.ALERT]: AlertProps
  [ComponentIdentifierEnum.RATING]: RatingProps
  [ComponentIdentifierEnum.TIMELINE]: TimelineProps
  [ComponentIdentifierEnum.EMPTY_STATE]: EmptyStateProps
  [ComponentIdentifierEnum.SKELETON]: SkeletonProps
  [ComponentIdentifierEnum.PROGRESS_RING]: ProgressRingProps
  [ComponentIdentifierEnum.DESCRIPTION_LIST]: DescriptionListProps
  [ComponentIdentifierEnum.TAG_GROUP]: TagGroupProps
  [ComponentIdentifierEnum.STEP_INDICATOR]: StepIndicatorProps
  [ComponentIdentifierEnum.CHART]: ChartProps

  [ComponentIdentifierEnum.CONTAINER]: ContainerProps
  [ComponentIdentifierEnum.CARD]: CardProps
  [ComponentIdentifierEnum.FORM]: FormProps
  [ComponentIdentifierEnum.COLLECTION]: CollectionProps
  [ComponentIdentifierEnum.COLLECTION_PROTOTYPE]: CollectionPrototypeProps
  [ComponentIdentifierEnum.TABLE]: TableProps
  [ComponentIdentifierEnum.TABLE_ROW]: TableRowProps
  [ComponentIdentifierEnum.TABLE_CELL]: TableCellProps
  [ComponentIdentifierEnum.TASK_MANAGEMENT]: TaskManagementProps
  [ComponentIdentifierEnum.PROCESS_RENDERER]: ProcessRendererProps
  [ComponentIdentifierEnum.PAGE]: PageProps
  [ComponentIdentifierEnum.ZONE]: ZoneProps
  [ComponentIdentifierEnum.MODAL]: ModalProps
  [ComponentIdentifierEnum.STEPPER]: StepperProps
  [ComponentIdentifierEnum.STEP]: StepProps
  [ComponentIdentifierEnum.TAB_BAR]: TabBarProps
  [ComponentIdentifierEnum.TAB]: TabProps
  [ComponentIdentifierEnum.CHAT]: ChatProps
  [ComponentIdentifierEnum.CHAT_HEADER]: ChatHeaderProps
  [ComponentIdentifierEnum.CHAT_AGENT_MESSAGE]: ChatAgentMessageProps
  [ComponentIdentifierEnum.CHAT_TEXT_INPUT]: ChatTextInputProps
  [ComponentIdentifierEnum.CHAT_ERROR_MESSAGE]: ChatErrorMessageProps
  [ComponentIdentifierEnum.CHAT_SUGGESTED_PROMPTS]: ChatSuggestedPromptsProps
  [ComponentIdentifierEnum.CHAT_HISTORY]: ChatHistoryProps
  [ComponentIdentifierEnum.CHAT_FILE_ATTACHMENTS]: ChatFileAttachmentsProps
  [ComponentIdentifierEnum.CHAT_FILE_ITEM]: ChatFileItemProps
  [ComponentIdentifierEnum.CHAT_USER_ATTACHED_FILES]: ChatUserAttachedFilesProps
  [ComponentIdentifierEnum.CHAT_USER_FILE_ITEM]: ChatUserFileItemProps
  [ComponentIdentifierEnum.CHAT_MESSAGE_ACTIONS]: ChatMessageActionsProps

  [FormFieldsEnum.INPUT]: InputProps
  [FormFieldsEnum.TEXTAREA]: TextareaProps
  [FormFieldsEnum.SELECT]: SelectProps
  [FormFieldsEnum.MULTI_SELECT]: MultiSelectProps
  [FormFieldsEnum.CHECKBOX]: CheckboxProps
  [FormFieldsEnum.RADIO]: RadioProps
  [FormFieldsEnum.SWITCH]: SwitchProps
  [FormFieldsEnum.DATEPICKER]: DatepickerProps
  [FormFieldsEnum.SEGMENTED_BUTTON]: SegmentedButtonProps
  [FormFieldsEnum.SLIDER]: SliderProps
}

export type TemplateConfigPropsByComponentType<T extends ComponentType> =
  T extends keyof TemplateConfigPropsMap ? TemplateConfigPropsMap[T] : never

export type TemplateConfigProps = TemplateConfigPropsMap[keyof TemplateConfigPropsMap]
